// PainelDono — o Relatório de Propósito, só com números.
//
// NÃO fica dentro da área logada do app de propósito: o painel é do DONO, e exigir
// conta de família (com assinatura ativa) só criava confusão — quem abria era
// mandado para o login. A proteção dele é a SENHA PRÓPRIA, conferida no servidor
// (RELATORIO_TOKEN). Sem ela, esta página não mostra nada além do campo de senha.
//
// Serve para responder duas perguntas do dono: "as conversas estão fluindo?" e
// "estamos cumprindo o propósito de aproximar as famílias de Deus com um
// entretenimento saudável?". Nenhum nome e nenhum texto de conversa aparece aqui:
// o backend conta tudo lá dentro e devolve só os números.
//
// Uma decisão que vem da pesquisa (UNICEF 2026; AIBM 2025): para criança, USO
// INTENSO NÃO É SUCESSO — é alerta. Por isso o bloco de saúde do uso acende um
// aviso quando o tempo sobe, em vez de comemorar.

import React, { useState, useEffect, useCallback } from 'react';
import { buscarRelatorio, guardarSenha, lerSenha, esquecerSenha } from 'services/painel';
import {
  Wrap, Titulo, Subtitulo, Aviso, Periodo, BotaoPeriodo, Bloco, TituloBloco,
  Numeros, Numero, Nota, Alerta, Tema, Pagina, PortaWrap, Campo, Botao, Erro, Sair,
} from './styled';

function Porta({ aoEntrar }) {
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState('');
  const entrar = () => {
    if (!senha.trim()) { setErro('Digite a senha do painel.'); return; }
    guardarSenha(senha.trim());
    aoEntrar();
  };
  return (
    <Pagina><PortaWrap>
      <Titulo>📊 Painel do Propósito</Titulo>
      <Subtitulo>Só para o dono. Digite a senha do painel.</Subtitulo>
      <Campo
        type="password"
        value={senha}
        placeholder="senha do painel"
        onChange={(e) => { setSenha(e.target.value); setErro(''); }}
        onKeyDown={(e) => { if (e.key === 'Enter') entrar(); }}
      />
      <Botao onClick={entrar}>Entrar</Botao>
      {erro ? <Erro>{erro}</Erro> : null}
      <Nota>A senha fica só nesta aba do navegador. Fechou, some.</Nota>
    </PortaWrap></Pagina>
  );
}

export default function DashboardPainelDono() {
  const [dias, setDias] = useState(30);
  const [estado, setEstado] = useState({ carregando: true });
  const [temSenha, setTemSenha] = useState(!!lerSenha());

  const carregar = useCallback(async () => {
    setEstado({ carregando: true });
    const r = await buscarRelatorio(dias);
    if (r.ok) setEstado({ dados: r.dados });
    else setEstado({ erro: r.motivo });
  }, [dias]);

  useEffect(() => { if (temSenha) carregar(); }, [temSenha, carregar]);

  if (!temSenha) {
    return <Porta aoEntrar={() => setTemSenha(true)} />;
  }

  const sair = () => { esquecerSenha(); setTemSenha(false); };

  if (estado.carregando) {
    return <Wrap><Subtitulo>Somando os números...</Subtitulo></Wrap>;
  }

  if (estado.erro) {
    const recados = {
      'senha-errada': 'Senha do painel incorreta — ou o painel ainda não foi ligado no servidor.',
      'sem-senha': 'Digite a senha do painel.',
      servidor: 'O servidor não conseguiu montar o relatório agora.',
      rede: 'Não consegui falar com o servidor. Confira sua internet.',
    };
    return (
      <Wrap>
          <Titulo>📊 Painel do Propósito</Titulo>
          <Erro>{recados[estado.erro] || 'Algo deu errado.'}</Erro>
          <Sair onClick={sair}>Trocar a senha do painel</Sair>
        </Wrap>
    );
  }

  const d = estado.dados || {};
  const escondido = d.fluindo && d.fluindo.disponivel === false;

  return (
    <Wrap>
        <Titulo>📊 Painel do Propósito</Titulo>
        <Subtitulo>
          As conversas estão fluindo? Estamos ajudando as famílias a se aproximarem de Deus,
          com um entretenimento saudável e seguro?
        </Subtitulo>

        <Aviso>
          🔒 <b>Sem dados de criança.</b> Nenhum nome e nenhum texto de conversa sai do servidor —
          aqui só chegam números somados. Recortes com poucas crianças ficam escondidos, para
          que ninguém possa ser identificado.
        </Aviso>

        <Periodo>
          {[7, 30, 90].map((n) => (
            <BotaoPeriodo key={n} ativo={dias === n} onClick={() => setDias(n)}>
              {n} dias
            </BotaoPeriodo>
          ))}
        </Periodo>

        {escondido ? (
          <Bloco>
            <TituloBloco>🌱 Ainda são poucas famílias</TituloBloco>
            <Nota>{d.aviso}</Nota>
          </Bloco>
        ) : (
          <>
            <Bloco>
              <TituloBloco>🌱 As conversas estão fluindo?</TituloBloco>
              <Numeros>
                <Numero cor="#eef7f0"><b>{d.criancas_ativas}</b><span>crianças conversando</span></Numero>
                <Numero cor="#eef1fb"><b>{d.fluindo.conversas}</b><span>conversas no período</span></Numero>
                <Numero cor="#eef1fb"><b>{d.fluindo.trocas_por_conversa}</b><span>falas da criança por conversa</span></Numero>
                <Numero cor="#fdf3e2"><b>{d.fluindo.parou_na_primeira_pct}%</b><span>pararam logo na primeira</span></Numero>
              </Numeros>
              <Nota>
                Modo Bíblico: {d.fluindo.por_modo.biblico} · Modo Idiomas: {d.fluindo.por_modo.idiomas}.
                Muitas conversas que param na primeira fala podem ser sinal de que o começo não engatou.
              </Nota>
            </Bloco>

            <Bloco>
              <TituloBloco>🙏 Propósito</TituloBloco>
              <Numeros>
                <Numero cor="#f3eefb"><b>{d.proposito.respostas_com_versiculo_pct}%</b><span>respostas com versículo</span></Numero>
              </Numeros>
              {d.proposito.temas_mais_conversados.length ? (
                <div style={{ marginTop: 12 }}>
                  {d.proposito.temas_mais_conversados.map((t) => (
                    <Tema key={t.tema}><span>{t.tema}</span><b>{t.vezes}</b></Tema>
                  ))}
                </div>
              ) : <Nota>Ainda sem versículos servidos no período.</Nota>}
            </Bloco>

            <Bloco>
              <TituloBloco>🛡️ Segurança</TituloBloco>
              <Numeros>
                <Numero cor="#fdeeea"><b>{d.seguranca.momentos_de_atencao}</b><span>momentos de atenção</span></Numero>
                <Numero cor="#f5f4fa"><b>{d.seguranca.bloqueios.historia_pesada}</b><span>histórias pesadas barradas</span></Numero>
                <Numero cor="#f5f4fa"><b>{d.seguranca.bloqueios.pedido_de_segredo}</b><span>pedidos de segredo barrados</span></Numero>
                <Numero cor="#f5f4fa"><b>{d.seguranca.bloqueios.falar_por_deus}</b><span>"recados de Deus" barrados</span></Numero>
              </Numeros>
              <Nota>
                "Momentos de atenção" são conversas em que a criança tocou num assunto sério —
                os pais veem isso na Área dos Pais. Aqui aparece só a quantidade.
              </Nota>
            </Bloco>

            <Bloco>
              <TituloBloco>⚠️ Saúde do uso</TituloBloco>
              <Numeros>
                <Numero cor="#eef1fb"><b>{d.saude_do_uso.minutos_por_conversa}</b><span>minutos por conversa</span></Numero>
                <Numero cor="#eef1fb"><b>{d.saude_do_uso.conversas_por_crianca_no_periodo}</b><span>conversas por criança</span></Numero>
                <Numero cor={d.saude_do_uso.dias_com_uso_intenso_pct > 10 ? '#fdf3e2' : '#eef7f0'}>
                  <b>{d.saude_do_uso.dias_com_uso_intenso_pct}%</b>
                  <span>dias acima de {d.saude_do_uso.limite_uso_intenso_min} min</span>
                </Numero>
              </Numeros>
              {d.saude_do_uso.dias_com_uso_intenso_pct > 10 ? (
                <Alerta>
                  ⚠️ Uso intenso subindo. Para criança, ficar muito tempo não é sinal de sucesso —
                  o nosso objetivo é família conversando, não criança presa na tela.
                </Alerta>
              ) : (
                <Nota>{d.saude_do_uso._nota}</Nota>
              )}
            </Bloco>

            <Bloco>
              <TituloBloco>🔧 Qualidade</TituloBloco>
              <Numeros>
                <Numero cor={d.qualidade.respostas_de_cortesia_pct > 3 ? '#fdeeea' : '#eef7f0'}>
                  <b>{d.qualidade.respostas_de_cortesia_pct}%</b><span>respostas que falharam</span>
                </Numero>
                <Numero cor="#f5f4fa"><b>{d.qualidade.convite_por_versiculo_faltando}</b><span>versículos que faltaram na lista</span></Numero>
                <Numero cor="#f5f4fa"><b>{d.qualidade.total_respostas}</b><span>respostas no total</span></Numero>
              </Numeros>
              <Nota>
                "Versículos que faltaram" mostra quantas vezes o mascote precisou convidar a ler
                em família porque o versículo ainda não está na lista aprovada. É a dica de quais
                aprovar depois.
              </Nota>
            </Bloco>
          </>
        )}

        <Sair onClick={sair}>Sair do painel</Sair>
      </Wrap>
  );
}
